import { useEffect, useRef } from "react";
import { GROUPS, KIND_GROUP } from "../lib/colors";
import { stepReplay, togglePlay } from "../lib/replayEngine";
import { useApp } from "../lib/store";
import { formatInstant } from "../lib/time";

export const ERAS: { label: string; a: number; b: number }[] = [
  { label: "All of history", a: -3000, b: 2030 },
  { label: "Ancient world", a: -1600, b: 500 },
  { label: "Middle Ages", a: 450, b: 1500 },
  { label: "Early modern", a: 1480, b: 1800 },
  { label: "19th century", a: 1790, b: 1914 },
  { label: "World wars", a: 1908, b: 1950 },
  { label: "Cold War era", a: 1945, b: 1992 },
  { label: "Since 1989", a: 1988, b: 2027 },
  { label: "Last 10 years", a: 2016.3, b: 2027.3 },
];

/** Playback, zoom and era shortcuts above the timeline, plus the playhead date. */
export function Controls() {
  const t = useApp((s) => s.t), span = useApp((s) => s.span), now = useApp((s) => s.now);
  const playing = useApp((s) => s.playing), speed = useApp((s) => s.speed);
  const { setSpeed, setSpan, setRange, setT } = useApp.getState();
  const raf = useRef<number | null>(null);

  // playback: one visible span per ~40 s at 1×; a replay runs at its own pace instead
  useEffect(() => {
    if (!playing) return;
    let last = performance.now();
    const step = (time: number) => {
      const st = useApp.getState();
      const dt = Math.min(0.25, (time - last) / 1000); // slow devices keep pace; a hidden tab does not jump
      last = time;
      if (st.replay) {
        stepReplay(dt);
        if (useApp.getState().playing) raf.current = requestAnimationFrame(step);
        return;
      }
      const next = st.t + (st.span / 40) * st.speed * dt;
      if (next >= st.now) { st.setT(st.now); st.setPlaying(false); return; }
      st.setT(next);
      raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => { if (raf.current != null) cancelAnimationFrame(raf.current); };
  }, [playing]);

  return (
    <div className="controls">
      <div className="controls-left">
        <button className="btn icon" onClick={togglePlay} aria-label={playing ? "Pause" : "Play"} data-testid="play">
          {playing ? "❚❚" : "▶"}
        </button>
        <button className="btn small" onClick={() => setSpeed(speed >= 4 ? 0.5 : speed * 2)} aria-label="Playback speed" title="Playback speed">{speed}×</button>
        <select className="era-select" aria-label="Jump to an era" value="" onChange={(e) => {
          const era = ERAS[Number(e.target.value)];
          if (era) { useApp.getState().endReplay(); setRange(era.a, Math.min(era.b, now + (era.b - era.a) * 0.5)); }
        }}>
          <option value="">Eras…</option>
          {ERAS.map((e, k) => <option key={e.label} value={k}>{e.label}</option>)}
        </select>
      </div>
      <div className="controls-date" aria-live="polite">{formatInstant(t, span)}</div>
      <div className="controls-right">
        <button className="btn icon" onClick={() => setSpan(span * 1.8)} aria-label="Zoom out in time">−</button>
        <button className="btn icon" onClick={() => setSpan(span / 1.8)} aria-label="Zoom in in time">+</button>
        <button className="btn small" onClick={() => { useApp.getState().endReplay(); setT(now); }} disabled={now - t < 1e-3}>Today</button>
      </div>
    </div>
  );
}

/** Colour legend; clicking a group shows or hides its conflicts. */
export function Legend() {
  const kinds = useApp((s) => s.kinds);
  const toggle = useApp((s) => s.toggleKindGroup);
  return (
    <div className="legend" aria-label="Legend">
      {GROUPS.map((g, k) => {
        const on = KIND_GROUP.some((grp, kind) => grp === k && kinds[kind]);
        return (
          <button key={g.key} className={`legend-item${on ? "" : " off"}`} onClick={() => toggle(k, KIND_GROUP)} aria-pressed={on}>
            <span className="dot" style={{ background: g.hex }} aria-hidden /><span className="lg-long">{g.label}</span><span className="lg-short">{g.short}</span>
          </button>
        );
      })}
    </div>
  );
}

export function Tooltip() {
  const h = useApp((s) => s.hover);
  if (!h) return null;
  return (
    <div className="map-tip" style={{ left: h.x + 14, top: h.y + 14 }} role="tooltip">
      <div className="tip-title">{h.title}</div>
      {h.lines.map((l, k) => <div key={k} className="tip-line">{l}</div>)}
    </div>
  );
}
